
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { LanguageProvider } from "./contexts/LanguageContext";

const GA_MEASUREMENT_ID = "G-GJE8WK72B7";
const Index = lazy(() => import("./pages/Index"));
const Toaster = lazy(() => import("@/components/ui/toaster").then((module) => ({ default: module.Toaster })));
const Privacy = lazy(() => import("./pages/Privacy"));
const ProductDetailPropertyprofi = lazy(() => import("./pages/ProductDetailPropertyprofi"));
const ProductDetailGym = lazy(() => import("./pages/ProductDetailGym"));
const ProductDetailWeb = lazy(() => import("./pages/ProductDetailWeb"));
const ProductDetailPortal = lazy(() => import("./pages/ProductDetailPortal"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

declare global {
  interface Window {
    INITSO_GA_ID?: string;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const HashScrollHandler = () => {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;

    const targetId = decodeURIComponent(location.hash.slice(1));
    const timeoutId = window.setTimeout(() => {
      const target = document.getElementById(targetId);
      if (!target) return;

      const targetTop = target.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: "smooth",
      });
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [location.pathname, location.hash]);

  return null;
};

const AnalyticsRouteTracker = () => {
  const location = useLocation();
  const hasSkippedInitialPageview = useRef(false);

  useEffect(() => {
    if (!hasSkippedInitialPageview.current) {
      hasSkippedInitialPageview.current = true;
      return;
    }

    const pagePath = `${location.pathname}${location.search}`;
    const timeoutId = window.setTimeout(() => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || ((...args: unknown[]) => {
        window.dataLayer?.push(args);
      });

      window.gtag("config", window.INITSO_GA_ID || GA_MEASUREMENT_ID, {
        page_path: pagePath,
        page_location: window.location.href,
        page_title: document.title,
      });
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [location.pathname, location.search]);

  return null;
};

const DeferredToaster = () => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const mountToaster = () => setMounted(true);

    window.addEventListener("pointerdown", mountToaster, { once: true });
    window.addEventListener("keydown", mountToaster, { once: true });

    return () => {
      window.removeEventListener("pointerdown", mountToaster);
      window.removeEventListener("keydown", mountToaster);
    };
  }, []);

  if (!mounted) return null;

  return (
    <Suspense fallback={null}>
      <Toaster />
    </Suspense>
  );
};

export const AppInner = () => (
  <LanguageProvider>
    <DeferredToaster />
    <HashScrollHandler />
    <AnalyticsRouteTracker />
    <Suspense fallback={null}>
      <Routes>
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/:locale/blog" element={<BlogIndex />} />
        <Route path="/:locale/blog/:slug" element={<BlogPost />} />
        <Route path="/" element={<Index />} />
        <Route path="/:locale" element={<Index />} />
        <Route path="/:locale/privacy" element={<Privacy />} />
        <Route path="/:locale/products/Propertyprofi" element={<ProductDetailPropertyprofi />} />
        <Route path="/:locale/products/gym" element={<ProductDetailGym />} />
        <Route path="/:locale/products/web" element={<ProductDetailWeb />} />
        <Route path="/:locale/products/portal" element={<ProductDetailPortal />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  </LanguageProvider>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
